import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiRequest } from "@/lib/queryClient";
import { insertAppointmentSchema, type Appointment, type Client, type Prospect } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Edit,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ExternalLink,
  Settings,
  Mail,
  Link,
  Sparkles,
  X,
  MapPin,
  ChevronDown,
} from "lucide-react";
import { useLocation } from "wouter";
import { z } from "zod";
import { toLocalDateTime } from "@/lib/utils";
import { CalendarAiInsights } from "@/components/calendar/CalendarAiInsights";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Form-specific schema that accepts string dates (from datetime-local input)
const appointmentFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  clientId: z.number().nullable().optional(),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
  status: z.string().default("scheduled"),
  type: z.string().min(1, "Type is required"),
  location: z.string().optional(),
});

type AppointmentForm = z.infer<typeof appointmentFormSchema>;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 24;
const DAY_MINUTES = 24 * 60;
const DAY_HEIGHT = (DAY_MINUTES / SLOT_MINUTES) * SLOT_HEIGHT;

interface SyncedCalendarEvent {
  id: number;
  calendarAccountId: number;
  providerEventId: string;
  title: string | null;
  description: string | null;
  location: string | null;
  startTime: string;
  endTime: string;
  isAllDay: boolean;
  status: string | null;
  htmlLink: string | null;
  syncDirection: string;
  linkedAppointmentId: number | null;
}

interface CalendarAccount {
  id: number;
  provider: "google" | "outlook";
  email: string;
  lastSyncedAt: string | null;
  isActive: boolean;
}

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<"month" | "week" | "day">("month");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [showSyncedEvents, setShowSyncedEvents] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedCalendarAccountId, setSelectedCalendarAccountId] = useState<number | undefined>();
  const [moreOpen, setMoreOpen] = useState(false);
  const [syncCooldown, setSyncCooldown] = useState(false);
  const [syncToCalendar, setSyncToCalendar] = useState(true);
  const [invitedTeamMembers, setInvitedTeamMembers] = useState<string[]>([]);
  const [manualInvites, setManualInvites] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const [previewEvent, setPreviewEvent] = useState<{
    title: string;
    startTime: string;
    endTime: string;
    isAllDay: boolean;
    description?: string | null;
    location?: string | null;
    htmlLink?: string | null;
    anchorRect: DOMRect;
  } | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [expandedDay, setExpandedDay] = useState<{
    date: Date;
    appointments: Appointment[];
    syncedEvents: SyncedCalendarEvent[];
    anchorRect: DOMRect;
  } | null>(null);
  const expandedDayRef = useRef<HTMLDivElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [previewAppointment, setPreviewAppointment] = useState<{
    appointment: Appointment;
    htmlLink: string;
    anchorRect: DOMRect;
  } | null>(null);
  const previewAppointmentRef = useRef<HTMLDivElement>(null);

  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem("calendar-onboarding-dismissed") === "true"
  );
  const [setupDismissed, setSetupDismissed] = useState(
    () => localStorage.getItem("calendar-setup-dismissed") === "true"
  );

  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    localStorage.setItem("calendar-onboarding-dismissed", "true");
  };

  const dismissSetup = () => {
    setSetupDismissed(true);
    localStorage.setItem("calendar-setup-dismissed", "true");
  };

  const openEventPreview = useCallback((event: SyncedCalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPreviewEvent({
      title: event.title || "(No title)",
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.isAllDay,
      description: event.description,
      location: event.location,
      htmlLink: event.htmlLink,
      anchorRect: rect,
    });
  }, []);

  // Close preview on click outside or Escape
  useEffect(() => {
    if (!previewEvent) return;
    const handleClick = (e: MouseEvent) => {
      if (previewRef.current && !previewRef.current.contains(e.target as Node)) {
        setPreviewEvent(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewEvent(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [previewEvent]);

  // Close expanded day popover on click outside or Escape
  useEffect(() => {
    if (!expandedDay) return;
    const handleClick = (e: MouseEvent) => {
      if (expandedDayRef.current && !expandedDayRef.current.contains(e.target as Node)) {
        setExpandedDay(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedDay(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [expandedDay]);

  // Close appointment preview on click outside or Escape
  useEffect(() => {
    if (!previewAppointment) return;
    const handleClick = (e: MouseEvent) => {
      if (previewAppointmentRef.current && !previewAppointmentRef.current.contains(e.target as Node)) {
        setPreviewAppointment(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewAppointment(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [previewAppointment]);

  useEffect(() => {
    if (viewMode !== "month" && !selectedDate) {
      setSelectedDate(new Date(currentDate));
    }
  }, [viewMode, selectedDate, currentDate]);

  const { data: appointments = [], isLoading: appointmentsLoading } = useQuery<Appointment[]>({
    queryKey: ["/api/appointments"],
  });

  const { data: teamMembers = [] } = useQuery<any[]>({
    queryKey: ["/api/agency/team-members"],
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const { data: prospects = [] } = useQuery<Prospect[]>({
    queryKey: ["/api/prospects"],
  });

  // Calendar accounts
  const { data: calendarAccountsData } = useQuery<{ accounts: CalendarAccount[] }>({
    queryKey: ["/api/calendar-accounts"],
    queryFn: async () => {
      const response = await fetch("/api/calendar-accounts");
      if (!response.ok) throw new Error("Failed to fetch calendar accounts");
      return response.json();
    },
  });

  const calendarAccounts = calendarAccountsData?.accounts || [];

  // Calendar providers
  const { data: providersData } = useQuery<{ providers: string[] }>({
    queryKey: ["/api/calendar-accounts/providers"],
    queryFn: async () => {
      const response = await fetch("/api/calendar-accounts/providers");
      if (!response.ok) throw new Error("Failed to fetch providers");
      return response.json();
    },
  });

  const availableProviders = providersData?.providers || [];

  // Set default selected account
  useEffect(() => {
    if (calendarAccounts.length > 0 && !selectedCalendarAccountId) {
      setSelectedCalendarAccountId(calendarAccounts[0].id);
    }
  }, [calendarAccounts, selectedCalendarAccountId]);

  // Synced calendar events
  const { data: syncedEventsData, isLoading: syncedEventsLoading } = useQuery<{
    events: SyncedCalendarEvent[];
    total: number;
  }>({
    queryKey: ["/api/synced-calendar-events", selectedCalendarAccountId],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "500" });
      if (selectedCalendarAccountId) {
        params.set("accountId", selectedCalendarAccountId.toString());
      }
      const response = await fetch(`/api/synced-calendar-events?${params}`);
      if (!response.ok) throw new Error("Failed to fetch synced events");
      return response.json();
    },
    enabled: calendarAccounts.length > 0,
  });

  const syncedEvents = syncedEventsData?.events || [];

  const syncedByAppointmentId = useMemo(() => {
    const map = new Map<number, SyncedCalendarEvent>();
    for (const event of syncedEvents) {
      if (event.linkedAppointmentId) {
        map.set(event.linkedAppointmentId, event);
      }
    }
    return map;
  }, [syncedEvents]);

  const form = useForm<AppointmentForm>({
    resolver: zodResolver(appointmentFormSchema),
    defaultValues: {
      title: "",
      description: "",
      clientId: null,
      startTime: "",
      endTime: "",
      status: "scheduled",
      type: "meeting",
      location: "",
    },
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async ({ accountId, forceFullSync }: { accountId: number; forceFullSync?: boolean }) => {
      const response = await fetch(`/api/calendar-accounts/${accountId}/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxResults: 200, forceFullSync }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Sync failed");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-accounts"] });
      toast({
        title: "Sync Complete",
        description: `Synced ${data.eventsSynced} events`,
      });
      setSyncCooldown(true);
      setTimeout(() => setSyncCooldown(false), 15_000);
    },
    onError: (error: Error) => {
      toast({
        title: "Sync Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await fetch(`/api/calendar-accounts/${accountId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to disconnect");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/synced-calendar-events"] });
      toast({
        title: "Account disconnected",
        description: "Calendar account has been removed",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to disconnect",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Push to calendar mutation
  const pushToCalendarMutation = useMutation({
    mutationFn: async ({ appointmentId, calendarAccountId }: { appointmentId: number; calendarAccountId: number }) => {
      const response = await fetch(`/api/appointments/${appointmentId}/push-to-calendar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ calendarAccountId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to push to calendar");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-calendar-events"] });
      toast({
        title: "Success",
        description: "Appointment pushed to external calendar",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createAppointmentMutation = useMutation({
    mutationFn: async (data: AppointmentForm) => {
      return apiRequest("POST", "/api/appointments", data);
    },
    onSuccess: async (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });

      // Push to external calendar when connected and opted-in
      if (syncToCalendar && selectedCalendarAccountId && result?.id) {
        try {
          await pushToCalendarMutation.mutateAsync({
            appointmentId: result.id,
            calendarAccountId: selectedCalendarAccountId,
          });
        } catch (err) {
          console.error("Failed to push to calendar:", err);
        }
      }

      setIsDialogOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "Appointment created successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create appointment",
        variant: "destructive",
      });
    },
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<AppointmentForm> }) => {
      return apiRequest("PUT", `/api/appointments/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      setIsDialogOpen(false);
      setEditingAppointment(null);
      form.reset();
      toast({
        title: "Success",
        description: "Appointment updated successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update appointment",
        variant: "destructive",
      });
    },
  });

  const deleteAppointmentMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/appointments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      toast({
        title: "Success",
        description: "Appointment deleted successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete appointment",
        variant: "destructive",
      });
    },
  });

  // Handle URL parameters for scheduling meetings
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldSchedule = urlParams.get('schedule');
    const type = urlParams.get('type');
    const id = urlParams.get('id');
    const name = urlParams.get('name');
    const email = urlParams.get('email');
    const phone = urlParams.get('phone');

    if (shouldSchedule === 'true' && type && id && name) {
      const clientId = type === 'client' ? parseInt(id) : null;
      const title = type === 'client'
        ? `Meeting with ${name}`
        : `Prospect Meeting - ${name}`;

      form.reset({
        title,
        description: `Meeting with ${name}${email ? ` (${email})` : ''}${phone ? ` - ${phone}` : ''}`,
        clientId,
        startTime: "",
        endTime: "",
        type: "meeting",
        status: "scheduled",
        location: "",
      });

      setIsDialogOpen(true);
      window.history.replaceState({}, '', '/calendar');
    }
  }, [location, form]);

  const toggleTeamMemberInvite = (memberId: string) => {
    setInvitedTeamMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const onSubmit = (data: AppointmentForm) => {
    const formattedData = {
      ...data,
      startTime: new Date(data.startTime).toISOString(),
      endTime: new Date(data.endTime).toISOString(),
      clientId: data.clientId || null,
      invitedTeamMembers,
      manualInvites,
    };

    if (editingAppointment) {
      updateAppointmentMutation.mutate({ id: editingAppointment.id, data: formattedData });
    } else {
      createAppointmentMutation.mutate(formattedData);
    }
  };

  const handleEdit = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    form.reset({
      title: appointment.title,
      description: appointment.description || "",
      clientId: appointment.clientId,
      startTime: toLocalDateTime(new Date(appointment.startTime)),
      endTime: toLocalDateTime(new Date(appointment.endTime)),
      status: appointment.status,
      type: appointment.type,
      location: appointment.location || "",
    });
    // Load existing invitees if present
    setInvitedTeamMembers((appointment as any).invitedTeamMembers || []);
    setManualInvites((appointment as any).manualInvites || "");
    setMoreOpen(true);
    setIsDialogOpen(true);
  };

  const handleAppointmentClick = useCallback((apt: Appointment, e: React.MouseEvent) => {
    e.stopPropagation();
    const syncedEvent = syncedByAppointmentId.get(apt.id);
    if (syncedEvent?.htmlLink) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setPreviewAppointment({ appointment: apt, htmlLink: syncedEvent.htmlLink, anchorRect: rect });
    } else {
      handleEdit(apt);
    }
  }, [syncedByAppointmentId]);

  const handleDelete = (id: number) => {
    setDeleteConfirmId(id);
  };

  const getClientName = (clientId: number | null) => {
    if (!clientId) return null;
    const client = clients.find(c => c.id === clientId);
    return client?.name || "Unknown Client";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "completed":
        return "status-active";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "status-inactive";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "meeting":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "call":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "review":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "consultation":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  const navigateView = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      const delta = direction === "prev" ? -1 : 1;

      if (viewMode === "month") {
        newDate.setMonth(newDate.getMonth() + delta);
      } else if (viewMode === "week") {
        newDate.setDate(newDate.getDate() + delta * 7);
      } else {
        newDate.setDate(newDate.getDate() + delta);
      }

      if (viewMode !== "month") {
        setSelectedDate(new Date(newDate));
      }

      return newDate;
    });
  };

  const navigateToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getWeekStart = (date: Date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const generateWeekDays = (date: Date) => {
    const start = getWeekStart(date);
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  };

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.startTime);
      return aptDate.toDateString() === date.toDateString();
    }).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  };

  const getSyncedEventsForDate = (date: Date) => {
    return syncedEvents.filter(event => {
      const eventDate = new Date(event.startTime);
      return eventDate.toDateString() === date.toDateString() && !event.linkedAppointmentId;
    }).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  };

  const getCombinedEventsForDate = (date: Date) => {
    const dayAppointments = getAppointmentsForDate(date).map((apt) => ({
      id: `apt-${apt.id}`,
      title: apt.title,
      startTime: apt.startTime,
      endTime: apt.endTime,
      isAllDay: false,
      type: "appointment" as const,
      _appointment: apt,
    }));

    const daySyncedEvents = (showSyncedEvents ? getSyncedEventsForDate(date) : []).map((event) => ({
      id: `sync-${event.id}`,
      title: event.title || "(No title)",
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.isAllDay,
      type: "synced" as const,
      description: event.description,
      location: event.location,
      htmlLink: event.htmlLink,
      _syncedEvent: event,
    }));

    return [...dayAppointments, ...daySyncedEvents].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const activeDate = selectedDate || currentDate;

  const isCurrentMonth = (date: Date) => {
    const referenceDate = viewMode === "month" ? currentDate : activeDate;
    return date.getMonth() === referenceDate.getMonth();
  };

  const formatHeaderDate = () => {
    if (viewMode === "month") {
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }

    if (viewMode === "week") {
      const weekStart = getWeekStart(activeDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const startLabel = weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const endLabel = weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${startLabel} - ${endLabel}`;
    }

    return activeDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getMinutesSinceStart = (dateString: string) => {
    const date = new Date(dateString);
    return date.getHours() * 60 + date.getMinutes();
  };

  const formatTimeLabel = (hour: number) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    return date.toLocaleTimeString("en-US", { hour: "numeric", hour12: true });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const openNewAppointmentDialog = (date?: Date) => {
    setEditingAppointment(null);
    const selectedDateTime = date || selectedDate || new Date();
    const startTime = new Date(selectedDateTime);
    startTime.setHours(9, 0, 0, 0);
    const endTime = new Date(startTime);
    endTime.setHours(10, 0, 0, 0);

    form.reset({
      title: "",
      description: "",
      clientId: null,
      startTime: toLocalDateTime(startTime),
      endTime: toLocalDateTime(endTime),
      status: "scheduled",
      type: "meeting",
      location: "",
    });
    setMoreOpen(false);
    setSyncToCalendar(true);
    setInvitedTeamMembers([]);
    setManualInvites("");
    setIsDialogOpen(true);
  };

  const calendarDays = generateCalendarDays();
  const viewDates =
    viewMode === "month"
      ? calendarDays
      : viewMode === "week"
        ? generateWeekDays(activeDate)
        : [activeDate];
  const isLoading = appointmentsLoading || syncedEventsLoading;

  return (
    <div className="space-y-6">
      {/* Onboarding Banner — First Visit */}
      {!onboardingDismissed && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-3">
                  <CalendarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Your Scheduling Hub
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    The calendar helps you stay on top of client meetings, renewal reviews, and follow-ups — all in one place. Connect your Google or Outlook calendar for two-way sync, get AI-powered scheduling suggestions, and never miss an important appointment.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    <div className="flex items-start gap-2">
                      <RefreshCw className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Two-Way Sync</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Appointments sync between administr and your calendar</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">AI Insights</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Get smart suggestions for follow-ups and conflict detection</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Link className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Client Linking</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Link appointments to clients for a complete activity history</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        dismissOnboarding();
                        if (calendarAccounts.length === 0) {
                          setSettingsOpen(true);
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Get Started
                    </Button>
                    <Button variant="outline" onClick={dismissOnboarding}>
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissOnboarding}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Calendar
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage appointments and schedule meetings
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Sync Button — single click incremental sync */}
          {calendarAccounts.length > 0 && selectedCalendarAccountId && (
            <Button
              variant="outline"
              size="sm"
              disabled={syncMutation.isPending || syncCooldown}
              onClick={() => syncMutation.mutate({ accountId: selectedCalendarAccountId })}
              title="Sync calendar"
            >
              <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? "animate-spin" : ""}`} />
              Sync
            </Button>
          )}

          <Select
            value={viewMode}
            onValueChange={(value: "month" | "week" | "day") => setViewMode(value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="day">Day</SelectItem>
            </SelectContent>
          </Select>

          {/* Settings Sheet */}
          <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Settings className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Calendar Settings</SheetTitle>
                <SheetDescription>
                  Manage your connected calendar accounts
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Display section */}
                {calendarAccounts.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Display</h3>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-synced-settings" className="text-sm">Show external calendar events</Label>
                      <Switch
                        id="show-synced-settings"
                        checked={showSyncedEvents}
                        onCheckedChange={setShowSyncedEvents}
                      />
                    </div>
                  </div>
                )}

                {/* Connected Accounts */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Connected Calendars</h3>
                  <p className="text-xs text-muted-foreground">
                    Your connected calendars sync bidirectionally — appointments you create here appear in your external calendar, and external events show up in administr.
                  </p>
                  {calendarAccounts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No calendars connected</p>
                  ) : (
                    calendarAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="space-y-2 p-3 border rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                account.provider === "google"
                                  ? "bg-red-100"
                                  : "bg-blue-100"
                              }`}
                            >
                              <CalendarIcon
                                className={`w-4 h-4 ${
                                  account.provider === "google"
                                    ? "text-red-500"
                                    : "text-blue-500"
                                }`}
                              />
                            </div>
                            <div>
                              <div className="text-sm font-medium">{account.email}</div>
                              <div className="text-xs text-muted-foreground capitalize">
                                {account.provider} Calendar
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => disconnectMutation.mutate(account.id)}
                            disabled={disconnectMutation.isPending}
                          >
                            Disconnect
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => syncMutation.mutate({ accountId: account.id, forceFullSync: true })}
                          disabled={syncMutation.isPending}
                        >
                          <RefreshCw className={`h-3 w-3 mr-1.5 ${syncMutation.isPending ? "animate-spin" : ""}`} />
                          Full Sync
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {/* Connect New Account */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Add Calendar</h3>
                  <p className="text-xs text-muted-foreground">
                    Connect your calendar to automatically sync appointments. We only access your calendar events — no emails or other data.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.location.href = "/api/calendar-accounts/oauth/google/start";
                      }}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 text-red-500" />
                      Google Calendar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.location.href = "/api/calendar-accounts/oauth/outlook/start";
                      }}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 text-blue-500" />
                      Outlook
                    </Button>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openNewAppointmentDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                New Appointment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>
                  {editingAppointment ? "Edit Appointment" : "New Appointment"}
                </DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto flex-1 -mx-6 px-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  {/* Primary fields — always visible */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Meeting title" autoFocus {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="startTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="endTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="meeting">Meeting</SelectItem>
                            <SelectItem value="call">Call</SelectItem>
                            <SelectItem value="review">Review</SelectItem>
                            <SelectItem value="consultation">Consultation</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Collapsible "More options" — collapsed on create, expanded on edit */}
                  <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" type="button" className="w-full justify-start px-0 text-sm text-muted-foreground hover:text-foreground">
                        <ChevronDown className={`h-4 w-4 mr-1.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                        More options
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-2">
                      {/* Status — only in edit mode */}
                      {editingAppointment && (
                        <FormField
                          control={form.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Status</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="scheduled">Scheduled</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={form.control}
                        name="clientId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Client (Optional)</FormLabel>
                            <Select
                              onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select client" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="none" disabled>No client</SelectItem>
                                {clients.map((client) => (
                                  <SelectItem key={client.id} value={client.id.toString()}>
                                    {client.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="location"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Location</FormLabel>
                            <FormControl>
                              <Input placeholder="Meeting location" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea placeholder="Meeting description" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Invite Team Members Section */}
                      {teamMembers.length > 0 && (
                        <div className="space-y-2">
                          <Label>Invite Team Members</Label>
                          <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                            {teamMembers.map((member: any) => (
                              <label key={member.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                                <Checkbox
                                  checked={invitedTeamMembers.includes(member.id)}
                                  onCheckedChange={() => toggleTeamMemberInvite(member.id)}
                                />
                                <span>{member.firstName} {member.lastName}</span>
                                <span className="text-gray-500 text-xs">{member.email}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Manual Invites */}
                      <div className="space-y-2">
                        <Label htmlFor="manualInvites">Additional Invites (emails, comma-separated)</Label>
                        <Input
                          id="manualInvites"
                          placeholder="email1@example.com, email2@example.com"
                          value={manualInvites}
                          onChange={(e) => setManualInvites(e.target.value)}
                        />
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Sync to calendar toggle */}
                  {!editingAppointment && calendarAccounts.length > 0 && selectedCalendarAccountId && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <Label htmlFor="sync-to-cal" className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                        <Link className="h-3.5 w-3.5" />
                        Sync to {calendarAccounts.find(a => a.id === selectedCalendarAccountId)?.provider === "google" ? "Google" : "Outlook"} Calendar
                      </Label>
                      <Switch
                        id="sync-to-cal"
                        checked={syncToCalendar}
                        onCheckedChange={setSyncToCalendar}
                      />
                    </div>
                  )}

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createAppointmentMutation.isPending || updateAppointmentMutation.isPending}
                    >
                      {editingAppointment ? "Update" : "Create Appointment"}
                    </Button>
                  </div>
                </form>
                </Form>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* AI Insights Panel */}
      {calendarAccounts.length > 0 && (
        <CalendarAiInsights calendarAccountId={selectedCalendarAccountId} />
      )}

      {/* Inline Setup Prompt — No Accounts Connected */}
      {calendarAccounts.length === 0 && !setupDismissed && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-3">
                  <Link className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Connect Your Calendar
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-2xl">
                    Sync your Google Calendar or Outlook to see all your appointments in one place. Two-way sync keeps everything updated — schedule meetings here and they appear in your calendar, and vice versa. Your calendar data stays private and is only used to help you manage your schedule.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        window.location.href = "/api/calendar-accounts/oauth/google/start";
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Connect Google Calendar
                    </Button>
                    <Button
                      onClick={() => {
                        window.location.href = "/api/calendar-accounts/oauth/outlook/start";
                      }}
                      variant="outline"
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Connect Outlook
                    </Button>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissSetup}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Calendar View */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <CalendarIcon className="h-5 w-5" />
                <span>{formatHeaderDate()}</span>
              </CardTitle>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={navigateToToday}>
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigateView("prev")}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigateView("next")}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : viewMode === "month" ? (
              <div className="grid grid-cols-7 gap-1">
                {/* Week day headers */}
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="p-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {viewDates.map((date, index) => {
                  const dayAppointments = getAppointmentsForDate(date);
                  const daySyncedEvents = showSyncedEvents ? getSyncedEventsForDate(date) : [];
                  const isCurrentMonthDay = isCurrentMonth(date);
                  const isTodayDate = isToday(date);
                  const totalEvents = dayAppointments.length + daySyncedEvents.length;

                  return (
                    <div
                      key={index}
                      className={`min-h-24 p-1 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        !isCurrentMonthDay ? "bg-gray-100 dark:bg-gray-800 text-gray-400" : ""
                      } ${
                        isTodayDate ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" : ""
                      }`}
                      onClick={() => {
                        setSelectedDate(date);
                        openNewAppointmentDialog(date);
                      }}
                    >
                      <div className={`text-sm font-medium mb-1 ${isTodayDate ? "text-blue-600 dark:text-blue-400" : ""}`}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {/* Internal appointments */}
                        {dayAppointments.slice(0, 2).map((apt) => (
                          <div
                            key={`apt-${apt.id}`}
                            className="text-xs p-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 truncate"
                            onClick={(e) => handleAppointmentClick(apt, e)}
                          >
                            {formatTime(apt.startTime)} {apt.title}
                          </div>
                        ))}
                        {/* Synced events - different styling */}
                        {daySyncedEvents.slice(0, 2 - dayAppointments.length).map((event) => (
                          <div
                            key={`sync-${event.id}`}
                            className="text-xs p-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 truncate border-l-2 border-purple-500"
                            onClick={(e) => openEventPreview(event, e)}
                          >
                            {formatTime(event.startTime)} {event.title || "(No title)"}
                          </div>
                        ))}
                        {totalEvents > 2 && (
                          <div
                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setExpandedDay({
                                date,
                                appointments: dayAppointments,
                                syncedEvents: daySyncedEvents,
                                anchorRect: rect,
                              });
                            }}
                          >
                            +{totalEvents - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-0">
                {/* Day headers row */}
                <div className={viewMode === "day" ? "grid grid-cols-[64px_1fr] gap-2" : "grid grid-cols-[64px_1fr] gap-2"}>
                  <div />
                  <div className={viewMode === "day" ? "grid grid-cols-1 gap-2" : "grid grid-cols-7 gap-2"}>
                    {(viewMode === "day" ? [activeDate] : generateWeekDays(activeDate)).map((date) => {
                      const isTodayDate = isToday(date);
                      return (
                        <div key={date.toISOString()} className={`text-xs font-medium text-center py-1 ${isTodayDate ? "text-blue-600" : "text-gray-600"}`}>
                          {WEEKDAYS[date.getDay()]} {date.getDate()}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* All-day events row (fixed height) */}
                {(() => {
                  const weekDates = viewMode === "day" ? [activeDate] : generateWeekDays(activeDate);
                  const hasAnyAllDay = weekDates.some((date) =>
                    getCombinedEventsForDate(date).some((e) => e.isAllDay)
                  );
                  if (!hasAnyAllDay) return null;
                  return (
                    <div className={viewMode === "day" ? "grid grid-cols-[64px_1fr] gap-2 mb-1" : "grid grid-cols-[64px_1fr] gap-2 mb-1"}>
                      <div className="text-xs text-gray-500 text-right pr-2 pt-1">All day</div>
                      <div className={viewMode === "day" ? "grid grid-cols-1 gap-2" : "grid grid-cols-7 gap-2"}>
                        {weekDates.map((date) => {
                          const allDayEvents = getCombinedEventsForDate(date).filter((e) => e.isAllDay);
                          return (
                            <div key={date.toISOString()} className="min-h-[24px] space-y-1">
                              {allDayEvents.map((event) => (
                                <div
                                  key={event.id}
                                  className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-100 truncate cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (event.type === "synced" && "_syncedEvent" in event) {
                                      openEventPreview((event as any)._syncedEvent, e);
                                    } else if (event.type === "appointment" && "_appointment" in event) {
                                      handleAppointmentClick((event as any)._appointment, e);
                                    }
                                  }}
                                >
                                  {event.title}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {/* Time grid */}
                <div className={viewMode === "day" ? "grid grid-cols-[64px_1fr] gap-2" : "grid grid-cols-[64px_1fr] gap-2"}>
                  <div>
                    {Array.from({ length: 24 }, (_, hour) => (
                      <div key={hour} style={{ height: SLOT_HEIGHT * (60 / SLOT_MINUTES) }} className="text-xs text-gray-500 text-right pr-2">
                        {formatTimeLabel(hour)}
                      </div>
                    ))}
                  </div>
                  <div className={viewMode === "day" ? "grid grid-cols-1 gap-2" : "grid grid-cols-7 gap-2"}>
                    {(viewMode === "day" ? [activeDate] : generateWeekDays(activeDate)).map((date) => {
                      const isTodayDate = isToday(date);
                      const timedEvents = getCombinedEventsForDate(date).filter((event) => !event.isAllDay);

                      return (
                        <div key={date.toISOString()}>
                          <div
                            className={`relative border border-gray-200 dark:border-gray-700 rounded ${
                              isTodayDate ? "bg-blue-50/30 dark:bg-blue-900/10" : "bg-white dark:bg-gray-900"
                            }`}
                            style={{ height: DAY_HEIGHT }}
                            onClick={() => {
                              setSelectedDate(date);
                              openNewAppointmentDialog(date);
                            }}
                          >
                            {Array.from({ length: DAY_MINUTES / SLOT_MINUTES }, (_, slot) => (
                              <div
                                key={slot}
                                className={`absolute left-0 right-0 border-t ${slot % (60 / SLOT_MINUTES) === 0 ? "border-gray-200 dark:border-gray-700" : "border-gray-100 dark:border-gray-800"}`}
                                style={{ top: slot * SLOT_HEIGHT }}
                              />
                            ))}
                            {timedEvents.map((event) => {
                              const startMinutes = getMinutesSinceStart(event.startTime);
                              const endMinutes = getMinutesSinceStart(event.endTime);
                              const top = (startMinutes / SLOT_MINUTES) * SLOT_HEIGHT;
                              const height = Math.max(18, ((endMinutes - startMinutes) / SLOT_MINUTES) * SLOT_HEIGHT);

                              return (
                                <div
                                  key={event.id}
                                  className={`absolute left-1 right-1 rounded px-2 py-1 text-xs shadow-sm cursor-pointer overflow-hidden ${
                                    event.type === "appointment"
                                      ? "bg-blue-500 text-white"
                                      : "bg-purple-500 text-white"
                                  }`}
                                  style={{ top, height }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (event.type === "synced" && "_syncedEvent" in event) {
                                      openEventPreview((event as any)._syncedEvent, e);
                                    } else if (event.type === "appointment" && "_appointment" in event) {
                                      handleAppointmentClick((event as any)._appointment, e);
                                    }
                                  }}
                                >
                                  <div className="font-medium truncate">{event.title}</div>
                                  {height > 30 && (
                                    <div className="opacity-90 truncate">
                                      {formatTime(event.startTime)} - {formatTime(event.endTime)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Appointments Sidebar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Upcoming</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {appointments
                .filter((apt) => new Date(apt.startTime) > new Date())
                .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                .slice(0, 5)
                .map((appointment) => (
                  <div
                    key={appointment.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {appointment.title}
                      </h4>
                      <div className="flex space-x-1">
                        {syncedByAppointmentId.get(appointment.id)?.htmlLink && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(syncedByAppointmentId.get(appointment.id)!.htmlLink!, "_blank")}
                            className="h-6 w-6 p-0 text-gray-500 hover:text-blue-600"
                            title="Open in Google Calendar"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(appointment)}
                          className="h-6 w-6 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(appointment.id)}
                          className="h-6 w-6 p-0 text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        {new Date(appointment.startTime).toLocaleDateString()}
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                      </div>
                      {appointment.location && <div>Location: {appointment.location}</div>}
                      {getClientName(appointment.clientId) && (
                        <div>Client: {getClientName(appointment.clientId)}</div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge className={getStatusColor(appointment.status)}>
                        {appointment.status}
                      </Badge>
                      <Badge className={getTypeColor(appointment.type)}>
                        {appointment.type}
                      </Badge>
                    </div>

                  </div>
                ))}

              {appointments.filter((apt) => new Date(apt.startTime) > new Date()).length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium text-gray-900 dark:text-gray-100">No upcoming appointments</p>
                  <p className="text-sm mt-1 max-w-xs mx-auto">
                    Schedule client meetings, renewal reviews, and follow-ups. Appointments linked to clients help you track your relationship activity.
                  </p>
                  <Button
                    className="mt-4"
                    size="sm"
                    onClick={() => openNewAppointmentDialog()}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Appointment
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Synced event preview popover */}
      {previewEvent && (
        <div
          ref={previewRef}
          className="fixed z-50 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4"
          style={{
            top: Math.min(previewEvent.anchorRect.bottom + 8, window.innerHeight - 280),
            left: Math.min(
              Math.max(previewEvent.anchorRect.left, 8),
              window.innerWidth - 336
            ),
          }}
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 pr-2">
              {previewEvent.title}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={() => setPreviewEvent(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {previewEvent.isAllDay ? (
                <span>All day</span>
              ) : (
                <span>{formatTime(previewEvent.startTime)} - {formatTime(previewEvent.endTime)}</span>
              )}
            </div>

            {previewEvent.location && (
              <div className="flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{previewEvent.location}</span>
              </div>
            )}

            {previewEvent.description && (
              <p className="text-gray-500 dark:text-gray-400 line-clamp-3 mt-1">
                {previewEvent.description}
              </p>
            )}
          </div>

          {previewEvent.htmlLink && (
            <Button
              size="sm"
              className="w-full mt-3"
              onClick={() => window.open(previewEvent.htmlLink!, "_blank")}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Open in Google Calendar
            </Button>
          )}
        </div>
      )}

      {/* Synced appointment preview popover */}
      {previewAppointment && (
        <div
          ref={previewAppointmentRef}
          className="fixed z-50 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4"
          style={{
            top: Math.min(previewAppointment.anchorRect.bottom + 8, window.innerHeight - 280),
            left: Math.min(
              Math.max(previewAppointment.anchorRect.left, 8),
              window.innerWidth - 336
            ),
          }}
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 pr-2">
              {previewAppointment.appointment.title}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={() => setPreviewAppointment(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span>{formatTime(previewAppointment.appointment.startTime)} - {formatTime(previewAppointment.appointment.endTime)}</span>
            </div>

            {previewAppointment.appointment.location && (
              <div className="flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{previewAppointment.appointment.location}</span>
              </div>
            )}

            {previewAppointment.appointment.description && (
              <p className="text-gray-500 dark:text-gray-400 line-clamp-3 mt-1">
                {previewAppointment.appointment.description}
              </p>
            )}
          </div>

          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => window.open(previewAppointment.htmlLink, "_blank")}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Google Calendar
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={() => {
                const apt = previewAppointment.appointment;
                setPreviewAppointment(null);
                handleEdit(apt);
              }}
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        </div>
      )}

      {/* Expanded day popover for "+X more" */}
      {expandedDay && (
        <div
          ref={expandedDayRef}
          className="fixed z-50 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4"
          style={{
            top: Math.min(expandedDay.anchorRect.bottom + 8, window.innerHeight - 350),
            left: Math.min(
              Math.max(expandedDay.anchorRect.left, 8),
              window.innerWidth - 336
            ),
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
              {expandedDay.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </h3>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => {
                  setExpandedDay(null);
                  setSelectedDate(expandedDay.date);
                  openNewAppointmentDialog(expandedDay.date);
                }}
              >
                <Plus className="h-3 w-3 mr-1" />
                New
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                onClick={() => setExpandedDay(null)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {expandedDay.appointments.map((apt) => (
              <div
                key={`apt-${apt.id}`}
                className="text-xs p-1.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 truncate"
                onClick={(e) => {
                  setExpandedDay(null);
                  handleAppointmentClick(apt, e);
                }}
              >
                {formatTime(apt.startTime)} {apt.title}
              </div>
            ))}
            {expandedDay.syncedEvents.map((event) => (
              <div
                key={`sync-${event.id}`}
                className="text-xs p-1.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 truncate border-l-2 border-purple-500"
                onClick={(e) => {
                  setExpandedDay(null);
                  openEventPreview(event, e);
                }}
              >
                {formatTime(event.startTime)} {event.title || "(No title)"}
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={deleteConfirmId !== null} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this appointment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteConfirmId !== null) {
                  deleteAppointmentMutation.mutate(deleteConfirmId);
                  setDeleteConfirmId(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
